// Capa de acceso a datos del módulo Empresas (4.2 del doc de arquitectura).
// Toda función de este archivo asume que quien la llama ya es super_admin
// (verificado por la guarda de ruta) — la protección real sigue siendo RLS,
// que aquí se manifiesta como "select *" simplemente funcionando sin
// filtrar por empresa_id, porque fn_es_super_admin() lo permite.

import { supabaseClient } from '@plataforma/supabase-client';

/**
 * @returns {Promise<{data: object[] | null, error: Error | null}>}
 */
export async function listarEmpresas() {
  return supabaseClient()
    .from('empresa')
    .select(
      'id, nombre, slug, subdominio, estado, moneda, creado_en, plan!empresa_plan_id_fkey (nombre)',
    )
    .is('eliminado_en', null)
    .order('creado_en', { ascending: false });
}

/**
 * @param {string} empresaId
 * @param {Record<string, unknown>} cambios ej. { estado: 'suspendida' }
 */
export async function actualizarEmpresa(empresaId, cambios) {
  return supabaseClient().from('empresa').update(cambios).eq('id', empresaId).select().single();
}

/**
 * Invoca la Edge Function `crear_empresa` (ver 8.1 del doc de arquitectura
 * y ADR-0006). No hay una versión "solo con el cliente" de esta operación:
 * crear el auth.user del titular requiere service_role, así que este es el
 * único camino posible desde el panel.
 *
 * @param {{
 *   nombre_empresa: string,
 *   slug: string,
 *   subdominio: string,
 *   plan_id: string,
 *   nombre_completo_titular: string,
 *   email_titular: string,
 * }} datos
 * @returns {Promise<{
 *   data: { empresa_id: string, usuario_id: string, email_titular: string, password_temporal: string } | null,
 *   error: { message: string } | null,
 * }>}
 */
export async function crearEmpresa(datos) {
  const { data, error } = await supabaseClient().functions.invoke('crear_empresa', {
    body: datos,
  });

  if (error) {
    // supabase-js entrega errores de Edge Function con el cuerpo de
    // respuesta accesible vía error.context — se intenta extraer el
    // mensaje específico que la función devolvió (400/403/409/500) para
    // mostrar algo más útil que "Edge Function returned a non-2xx status
    // code".
    let mensaje = error.message;
    try {
      const cuerpo = await error.context?.json?.();
      if (cuerpo?.error) {
        mensaje = cuerpo.detalles?.length
          ? `${cuerpo.error} ${cuerpo.detalles.join(' ')}`
          : cuerpo.error;
      }
    } catch {
      // Sin cuerpo JSON legible — se conserva error.message tal cual.
    }
    return { data: null, error: { message: mensaje } };
  }

  return { data, error: null };
}

/**
 * Chequeo de disponibilidad de slug/subdominio mientras el usuario escribe
 * en el formulario de alta — ver `debounce` en @plataforma/utils. Es un
 * chequeo de UX, no la fuente de verdad (esa es el índice único parcial de
 * `empresa` — ver 5.5 del doc de BD); `crearEmpresa` puede seguir fallando
 * por 409 si hubo una condición de carrera entre este chequeo y el submit.
 *
 * @param {string} slug
 * @param {string} subdominio
 * @returns {Promise<boolean>} true si AMBOS están disponibles.
 */
export async function estaDisponible(slug, subdominio) {
  const { data: empresa } = await supabaseClient()
    .from('empresa')
    .select('id')
    .or(`slug.ilike.${slug},subdominio.ilike.${subdominio}`)
    .is('eliminado_en', null)
    .maybeSingle();

  if (empresa) return false;

  try {
    const { data: proxy } = await supabaseClient()
      .from('proxy_reservado')
      .select('hostname')
      .or(`hostname.ilike.${subdominio},hostname.ilike.${subdominio}.vimosdigital.com`)
      .maybeSingle();

    return !proxy;
  } catch {
    return true;
  }
}
